import { gql } from '@apollo/client';

import {
  LINK_ADDRESSES_FIELDS,
  SMS_CAMPAIGN_FIELDS,
  SMS_CONNECTION_CHECK_FIELDS,
  SMS_MESSAGE_FIELDS,
  SMS_PROVIDER_STATUS_FIELDS,
  SMS_REMINDER_PLAN_FIELDS,
  SMS_SETTINGS_FIELDS,
  STUDENT_SMS_STATE_FIELDS,
  TEACHER_SMS_STATE_FIELDS,
} from '../fragments/sms';

/**
 * ТЗ 0081 — SMS студентам: мутации. Имена операций отличаются от полей
 * бэкенда. Каждая возвращает конверт `success` + `customErrors` (OutputMixin):
 * текст ошибки сервера показывается человеку, если своего перевода кода нет.
 */

/** SMS студенту; `requestKey` гасит двойной клик и повтор после обрыва сети. */
export const QueueStudentSmsMutation = gql`
  ${STUDENT_SMS_STATE_FIELDS}
  mutation QueueStudentSms(
    $studentId: ID!
    $text: String!
    $link: SmsLinkInput
    $templateId: ID
    $requestKey: String
  ) {
    sendStudentSms(
      studentId: $studentId
      text: $text
      link: $link
      templateId: $templateId
      requestKey: $requestKey
    ) {
      success
      customErrors {
        field
        code
        message
      }
      message {
        ...SmsMessageFields
      }
      state {
        ...StudentSmsStateFields
      }
    }
  }
`;

/**
 * SMS преподавателю; ссылка — на подпись его документа, LMS или свой адрес.
 * `requestKey` гасит двойной клик и повтор после обрыва сети.
 */
export const QueueTeacherSmsMutation = gql`
  ${TEACHER_SMS_STATE_FIELDS}
  mutation QueueTeacherSms(
    $teacherId: ID!
    $text: String!
    $link: TeacherSmsLinkInput
    $templateId: ID
    $requestKey: String
  ) {
    sendTeacherSms(
      teacherId: $teacherId
      text: $text
      link: $link
      templateId: $templateId
      requestKey: $requestKey
    ) {
      success
      customErrors {
        field
        code
        message
      }
      message {
        ...SmsMessageFields
      }
      state {
        ...TeacherSmsStateFields
      }
    }
  }
`;

/**
 * Рассылка по группе: у каждого студента своя SMS и своя ссылка.
 * `studentIds` — выбор сотрудника (не задан — все кандидаты режима).
 */
export const LaunchGroupSmsMutation = gql`
  ${SMS_CAMPAIGN_FIELDS}
  mutation LaunchGroupSms(
    $groupId: ID!
    $text: String!
    $title: String
    $link: SmsLinkInput
    $mode: String
    $templateId: ID
    $requestKey: String
    $studentIds: [ID!]
  ) {
    sendGroupSms(
      groupId: $groupId
      text: $text
      title: $title
      link: $link
      mode: $mode
      templateId: $templateId
      requestKey: $requestKey
      studentIds: $studentIds
    ) {
      success
      customErrors {
        field
        code
        message
      }
      campaign {
        ...SmsCampaignFields
      }
    }
  }
`;

/** Повторить рассылку тем, у кого SMS упала или пропущена устранимо. */
export const RepeatSmsCampaignMutation = gql`
  ${SMS_CAMPAIGN_FIELDS}
  mutation RepeatSmsCampaign($campaignId: ID!) {
    resendSmsCampaign(campaignId: $campaignId) {
      success
      customErrors {
        field
        code
        message
      }
      created
      campaign {
        ...SmsCampaignFields
      }
    }
  }
`;

/** Повторить одну SMS из журнала — ответ: НОВАЯ строка очереди. */
export const RequeueSmsMessageMutation = gql`
  ${SMS_MESSAGE_FIELDS}
  mutation RequeueSmsMessage($messageId: ID!) {
    retrySmsMessage(messageId: $messageId) {
      success
      customErrors {
        field
        code
        message
      }
      message {
        ...SmsMessageFields
      }
    }
  }
`;

/** Снять SMS, пока она ждёт в очереди. */
export const WithdrawSmsMessageMutation = gql`
  ${SMS_MESSAGE_FIELDS}
  mutation WithdrawSmsMessage($messageId: ID!) {
    cancelSmsMessage(messageId: $messageId) {
      success
      customErrors {
        field
        code
        message
      }
      message {
        ...SmsMessageFields
      }
    }
  }
`;

export const StoreSmsSettingsMutation = gql`
  ${SMS_SETTINGS_FIELDS}
  mutation StoreSmsSettings($input: SmsSettingsInput!) {
    saveSmsSettings(input: $input) {
      success
      customErrors {
        field
        code
        message
      }
      smsSettings {
        ...SmsSettingsFields
      }
    }
  }
`;

/**
 * ТЗ 0084: тексты канала, которые правят Admin и продавцы, — хвост отписки
 * рассылки по группе. Пусто — текст по умолчанию.
 */
export const StoreSmsTextsMutation = gql`
  ${SMS_SETTINGS_FIELDS}
  mutation StoreSmsTexts($optOutSuffix: String!) {
    saveSmsTexts(optOutSuffix: $optOutSuffix) {
      success
      customErrors {
        field
        code
        message
      }
      smsSettings {
        ...SmsSettingsFields
      }
    }
  }
`;

/**
 * ТЗ 0084: адрес анкеты дофинансирования и LMS. Не передано — не меняется;
 * пустая строка — вернуть значение из env.
 */
export const StoreLinkAddressesMutation = gql`
  ${LINK_ADDRESSES_FIELDS}
  mutation StoreLinkAddresses($dofinFormUrl: String, $lmsBaseUrl: String) {
    saveLinkAddresses(dofinFormUrl: $dofinFormUrl, lmsBaseUrl: $lmsBaseUrl) {
      success
      customErrors {
        field
        code
        message
      }
      linkAddresses {
        ...LinkAddressesFields
      }
    }
  }
`;

/** Лесенка целиком: шаги, которых нет во входе, удаляются. */
export const StoreSmsReminderPlanMutation = gql`
  ${SMS_REMINDER_PLAN_FIELDS}
  mutation StoreSmsReminderPlan(
    $topic: String!
    $enabled: Boolean!
    $steps: [ReminderStepInput!]!
  ) {
    saveReminderPlan(topic: $topic, enabled: $enabled, steps: $steps) {
      success
      customErrors {
        field
        code
        message
      }
      plan {
        ...SmsReminderPlanFields
      }
    }
  }
`;

/** `test=1` в SMSAPI: без отправки и без списания; заодно баланс и имя. */
export const ProbeSmsConnectionMutation = gql`
  ${SMS_CONNECTION_CHECK_FIELDS}
  ${SMS_PROVIDER_STATUS_FIELDS}
  mutation ProbeSmsConnection($to: String!) {
    checkSmsConnection(to: $to) {
      success
      customErrors {
        field
        code
        message
      }
      result {
        ...SmsConnectionCheckFields
      }
      status {
        ...SmsProviderStatusFields
      }
    }
  }
`;

/** Настоящая тестовая SMS — ОТПРАВЛЯЕТСЯ и стоит денег. */
export const DispatchSmsTestMutation = gql`
  ${SMS_CONNECTION_CHECK_FIELDS}
  mutation DispatchSmsTest($to: String!, $text: String) {
    sendSmsTest(to: $to, text: $text) {
      success
      customErrors {
        field
        code
        message
      }
      result {
        ...SmsConnectionCheckFields
      }
    }
  }
`;
